import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {useActionMenuTokenInputData} from '#/components/actionmenu/token-input/use-action-menu-token-input-data';
import {MessageCase} from '#/components/actionmenu/types';
import environment from '#/environment/environment';
import {useAddFundsAction} from '#/hooks/use-add-funds-action';
import {useDebounceValue} from '#/hooks/use-debounce-value';
import {MessageContent} from '#/repository/types';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {collateralRatioMinimumPercentage, collateralRatioTresholdPercentage} from '@shareland/shareland-common';
import {TFunction} from 'i18next';
import {useContext} from 'react';
import {useTranslation} from 'react-i18next';

type T = TFunction<'translation'>;

const notEnoughTokenAmount = (t: T, currencySymbol: string): MessageContent => ({
  content: t('noticeMessages.notEnoughTokenAmount', {currencySymbol}),
  theme: 'warning',
  canSubmitForm: false,
});
const notEnoughUsdc = (t: T, action?: () => void): MessageContent => ({
  content: t('noticeMessages.notEnoughUSDC'),
  theme: 'warning',
  canSubmitForm: false,
  actionButtonTitle: t('actions.addFunds'),
  action,
});
const tokenAmountRequiredForAction = (t: T, amount: number | string, currencySymbol: string): MessageContent => ({
  content: t('noticeMessages.tokenAmountRequiredForAction', {currencySymbol, amount}),
  theme: 'warning',
  canSubmitForm: false,
});
const valueMustBeWithinRange = (t: T, value: string, minValue: number, maxValue: number): MessageContent => ({
  content: t('noticeMessages.valueMustBeWithinRange', {value, minValue, maxValue}),
  theme: 'warning',
  canSubmitForm: false,
});
const marketIsHalted = (t: T): MessageContent => ({
  content: t('noticeMessages.marketIsHalted'),
  theme: 'warning',
  canSubmitForm: false,
});
const notEnoughLiquidity = (t: T): MessageContent => ({
  content: t('noticeMessages.notEnoughLiquidity'),
  theme: 'warning',
  canSubmitForm: false,
});
const transactionLikelyToFailMaxSlippageValueLower = (t: T, value: number): MessageContent => ({
  content: t('noticeMessages.transactionLikelyToFailMaxSlippageValueLower', {value, decimals: 0}),
  theme: 'warning',
  canSubmitForm: true,
});
const limitBuyMightProcessAsMarketOrder = (t: T): MessageContent => ({
  content: t('noticeMessages.limitBuyMightProcessAsMarketOrder'),
  theme: 'warning',
  canSubmitForm: true,
});
const limitSellMightProcessAsMarketOrder = (t: T): MessageContent => ({
  content: t('noticeMessages.limitSellMightProcessAsMarketOrder'),
  theme: 'warning',
  canSubmitForm: true,
});
const transactionFeesHigherThanReceivedAmount = (t: T): MessageContent => ({
  content: t('noticeMessages.transactionFeesHigherThanReceivedAmount'),
  theme: 'warning',
  canSubmitForm: false,
});
const mustBorrowInsteadOfBuy = (
  t: T,
  landTokenSymbol: string,
  currencySymbol: string,
  amount: string,
  action: () => void,
): MessageContent => ({
  content: t('noticeMessages.mustBorrowInsteadOfBuy', {landTokenSymbol, currencySymbol, amount}),
  theme: 'warning',
  canSubmitForm: false,
  actionButtonTitle: t('transactions.borrowOpen.present'),
  action,
});
const needToBuyOrBorrowToSell = (t: T, landTokenSymbol: string, action: () => void): MessageContent => ({
  content: t('noticeMessages.needToBuyOrBorrowToSell', {landTokenSymbol}),
  theme: 'warning',
  canSubmitForm: false,
  actionButtonTitle: t('transactions.buy.present'),
  action,
});
const needToBuyOrBorrowToRent = (t: T, landTokenSymbol: string, action: () => void): MessageContent => ({
  content: t('noticeMessages.needToBuyOrBorrowToRent', {landTokenSymbol}),
  theme: 'warning',
  canSubmitForm: false,
  actionButtonTitle: t('transactions.buy.present'),
  action,
});
const collateralRatioBelowMinimum = (t: T, value: number): MessageContent => ({
  content: t('noticeMessages.collateralRatioBelowMinimum', {
    collateralRatioMinimumPercentage,
    value,
  }),
  theme: 'error',
  canSubmitForm: false,
});
const collateralRatioBelowSuggestion = (t: T, value: number, canSubmitForm: boolean): MessageContent => ({
  content: t('noticeMessages.collateralRatioBelowSuggestion', {
    collateralRatioTresholdPercentage,
    value,
  }),
  theme: 'warning',
  canSubmitForm,
});
const needMoreLandTokenToCloseCollateralPosition = (t: T, currencySymbol: string, amount: number): MessageContent => ({
  content: t('noticeMessages.needMoreLandTokenToCloseCollateralPosition', {currencySymbol, amount}),
  theme: 'warning',
  canSubmitForm: false,
});
const mustRentToStopRent = (t: T, landTokenSymbol: string, action: () => void): MessageContent => ({
  content: t('noticeMessages.mustRentToStopRent', {landTokenSymbol}),
  theme: 'warning',
  canSubmitForm: false,
  actionButtonTitle: t('transactions.rent.present'),
  action,
});
const mustRentToCollectRewards = (t: T, landTokenSymbol: string): MessageContent => ({
  content: t('noticeMessages.mustRentToCollectRewards', {landTokenSymbol}),
  theme: 'warning',
  canSubmitForm: false,
});
const allRewardsAreClaimed = (t: T): MessageContent => ({
  content: t('noticeMessages.allRewardsAreClaimed'),
  theme: 'warning',
  canSubmitForm: false,
});
const inputIsInvalid = (content: string): MessageContent => ({
  content,
  theme: 'error',
  canSubmitForm: false,
});

export const actionMenuMessageContent = {
  notEnoughTokenAmount,
  notEnoughUsdc,
  tokenAmountRequiredForAction,
  valueMustBeWithinRange,
  marketIsHalted,
  notEnoughLiquidity,
  transactionLikelyToFailMaxSlippageValueLower,
  limitBuyMightProcessAsMarketOrder,
  limitSellMightProcessAsMarketOrder,
  mustBorrowInsteadOfBuy,
  needToBuyOrBorrowToSell,
  needToBuyOrBorrowToRent,
  collateralRatioBelowMinimum,
  collateralRatioBelowSuggestion,
  needMoreLandTokenToCloseCollateralPosition,
  mustRentToStopRent,
  mustRentToCollectRewards,
  allRewardsAreClaimed,
  transactionFeesHigherThanReceivedAmount,
  inputIsInvalid,
};

const messageCasesToContent = (cases: Record<string, MessageCase>): MessageContent[] =>
  Object.values(cases)
    .filter(elem => elem.condition())
    .map(elem => elem.message());

export const useMessageCasesToContent = (
  cases: Record<string, MessageCase>,
  options: {
    usdcAmount?: number;
    inputValidationMessage?: ReturnType<typeof useActionMenuTokenInputData>['tokenInput']['validationMessage'];
  } = {},
): MessageContent[] => {
  const {usdcAmount, inputValidationMessage} = options;

  const {landMarketInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');

  const addFundsAction = useAddFundsAction(usdcAmount ? usdcAmount / decimalsMultiplier : undefined);

  const debouncedUsdcAmount = useDebounceValue({
    value: usdcAmount,
    ms: usdcAmount === 0 ? 0 : 500,
    deps: [usdcAmount],
  });

  const defaultCases = {
    inputIsInvalid: {
      condition: () => !!inputValidationMessage,
      message: () => actionMenuMessageContent.inputIsInvalid(inputValidationMessage!),
    },
    ...(environment.isWaitlistNet && {
      addFunds: {
        condition: () => !!debouncedUsdcAmount,
        message: () => actionMenuMessageContent.notEnoughUsdc(t, addFundsAction),
      },
    }),
    marketHalted: {
      condition: () => landMarketInfo.paused,
      message: () => actionMenuMessageContent.marketIsHalted(t),
    },
  };

  const allCases = {
    ...defaultCases,
    ...cases,
  };

  return messageCasesToContent(allCases);
};
